/**
 * Step 4 — Sign in.
 *
 * Reuses the app's existing login flow (dispatches LOGIN_START; the redux-saga
 * performs the POST, writes localStorage, the follow-up cascade, AND seeds the
 * encrypted credential vault for offline login) and watches auth state for the
 * result, then advances to the data download.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import sx from '../setupStyles';
import authActions from '../../layouts/Auth/redux/actions';
import { authApi } from '../desktopApi';

/**
 * Wait until the login saga has seeded the main-process credential vault with a
 * usable (non-expired) token. The saga seeds the vault only AFTER LOGIN_SUCCESS
 * (it first runs the role / business-profile / subscription / parameters
 * cascade), so the token is NOT yet present the instant `loggedInUser` appears.
 * The next step (Sync) authenticates its master-data download from this vault, so
 * advancing before the seed lands sends those requests unauthenticated.
 *
 * Bounded so a missing OS keychain (token never persisted) can't hang the wizard —
 * the Sync step keeps its own retry + error UI as the final backstop.
 */
async function waitForVaultToken({ timeoutMs = 10000, intervalMs = 200 } = {}) {
  if (!authApi) return;
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    try {
      const session = await authApi.getSession();
      if (session && session.hasToken && !session.expired) return;
    } catch (_) {
      // Bridge hiccup — fall through to the timeout guard and let Sync recover.
    }
    if (Date.now() >= deadline) return;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

export default function Login({ goBack, goNext, patchCtx }) {
  const dispatch = useDispatch();
  const { loginLoader, loggedInUser, loginError } = useSelector((s) => s.authReducer);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const attempted = useRef(false);
  const handled = useRef(false);

  const submit = () => {
    if (!email.trim() || !password) {
      setFormError('Please enter your email and password.');
      return;
    }
    setFormError('');
    attempted.current = true;
    handled.current = false;
    dispatch({ type: authActions.LOGIN_START, loginData: { email: email.trim(), password } });
  };

  useEffect(() => {
    if (!attempted.current || loginLoader || handled.current) return;

    if (loggedInUser) {
      handled.current = true;
      // Wait for the login saga to finish seeding the encrypted vault before
      // advancing, so the data-download step has the auth token it needs.
      (async () => {
        await waitForVaultToken();
        patchCtx({ authenticated: true });
        goNext();
      })();
    } else if (loginError) {
      const msg =
        typeof loginError === 'string'
          ? loginError
          : (loginError && loginError.error && loginError.error.message) ||
            'Login failed. Please check your credentials.';
      setFormError(msg);
    } else {
      // Loader cleared but neither token nor error arrived (e.g. empty token):
      // surface feedback so the user is never silently stranded.
      setFormError('Login did not complete. Please try again.');
    }
  }, [loginLoader, loggedInUser, loginError, email, password, goNext, patchCtx]);

  return (
    <>
      <Box sx={sx.body}>
        <Box sx={{ ...sx.centerCol, gap: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Sign in to continue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We need to sign you in to download your data and enable offline access.
          </Typography>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Mobile Number or Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            fullWidth
          />
        </Box>
      </Box>
      <Box sx={sx.footer}>
        <Button color="inherit" onClick={goBack} disabled={loginLoader}>
          Back
        </Button>
        <Button variant="contained" onClick={submit} disabled={loginLoader}>
          {loginLoader ? 'Signing in…' : 'Sign in & Continue'}
        </Button>
      </Box>
    </>
  );
}
